import { Link } from "@tanstack/react-router";
import { RotateCw } from "lucide-react";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import { statusIntent, statusLabel } from "@/design";
import type { IntentName } from "@/design";
import { useIsAdmin } from "@/shared/auth";
import { EmptyState } from "@/shared/components/EmptyState";
import { MarketplaceChip } from "@/shared/components/MarketplaceChip";
import { Pagination } from "@/shared/components/Pagination";
import { SectionCard } from "@/shared/components/SectionCard";
import { toast } from "@/shared/lib/toast";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Tooltip } from "@/shared/ui/tooltip";

import { useFulfillment } from "../api/use-fulfillment";
import { useRetryAwb } from "../api/use-retry-awb";
import { formatAge } from "../lib/age";
import { groupStuck, stuckCause } from "../lib/stuck-cause";

import { StuckCauseCards } from "./StuckCauseCards";

const TH_COL =
  "px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]";
const TD = "px-4 py-3 align-middle text-sm";
const PAGE_SIZES = [10, 25, 50];

/** RTS SLA hours-left: null when there is no `ship_by` to measure against. */
function slaHours(shipBy: string | null, now: Date): number | null {
  if (shipBy === null) return null;
  return (new Date(shipBy).getTime() - now.getTime()) / 3_600_000;
}

/** danger <2h left, warning <24h, success otherwise — "time until", unlike
 * `age.ts`'s `ageIntent` which measures "time since". */
function slaIntent(hours: number | null): IntentName {
  if (hours === null) return "neutral";
  if (hours < 2) return "danger";
  if (hours < 24) return "warning";
  return "success";
}

type StuckOrder = NonNullable<ReturnType<typeof useFulfillment>["data"]>["stuck_orders"][number];

function StuckRow({
  order,
  now,
  showSla,
}: {
  order: StuckOrder;
  now: Date;
  showSla: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation("dashboard");
  const retryAwb = useRetryAwb();
  // POST /v1/orders/{id}/fetch-awb is admin-only — hidden for non-admins
  // rather than shown-and-403ing. onError covers a mid-session revoke.
  const isAdmin = useIsAdmin();

  const tAge = (key: string, options?: Record<string, unknown>): string =>
    options === undefined ? t(key) : t(key, options);

  const hours = slaHours(order.ship_by, now);
  const slaText =
    hours === null
      ? t("dashboard:age.none")
      : t("dashboard:stuck.sla_hours", { h: Math.trunc(hours) });
  const cause = stuckCause(order);

  return (
    <tr
      className="border-b border-[var(--row-divider)] transition-colors last:border-b-0 hover:bg-[var(--row-hover)]"
      data-testid="stuck-row"
    >
      <td className={TD}>
        <div className="flex flex-col items-start gap-1">
          <Link
            to="/orders/$saleOrderId"
            params={{ saleOrderId: order.sale_order_id }}
            className="font-mono text-xs font-semibold text-[var(--brand-600)] hover:underline"
            data-testid="stuck-order-link"
          >
            {order.sale_order_id}
          </Link>
          <MarketplaceChip marketplace={order.marketplace} />
        </div>
      </td>
      <td className={TD}>
        <div className="flex flex-col items-start gap-0.5">
          <Badge intent={statusIntent("order", order.lifecycle_status)}>
            {statusLabel("order", order.lifecycle_status, i18n.language)}
          </Badge>
          <span className="text-xs text-[var(--text-subtle)]">{order.marketplace_status}</span>
        </div>
      </td>
      <td className={TD}>
        <div className="font-semibold text-[var(--text-primary)]" data-testid="stuck-age">
          {formatAge(order.picked_at, now, tAge)}
        </div>
        <div className="text-xs text-[var(--text-subtle)]" data-testid="stuck-picked">
          {t("dashboard:stuck.picked_at", {
            time: new Date(order.picked_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          })}
        </div>
      </td>
      {showSla && (
        <td className={TD} data-testid="stuck-sla">
          <Badge intent={slaIntent(hours)}>{slaText}</Badge>
        </td>
      )}
      <td className={`${TD} max-w-[360px]`} data-testid="stuck-attempts">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-[var(--text-primary)]">
            {t(`dashboard:stuck.cause.${cause}`)}
          </span>
          <span className="text-xs text-[var(--text-subtle)]">
            {t("dashboard:stuck.attempts_n", { n: order.awb_attempts })}
          </span>
        </div>
        {order.awb_last_error !== "" && (
          <div
            className="truncate font-mono text-[11px] text-[var(--text-subtle)]"
            title={order.awb_last_error}
          >
            {order.awb_last_error}
          </div>
        )}
      </td>
      <td className={`${TD} w-12 text-right`}>
        {isAdmin && cause !== "pending" && (
          <Tooltip content={t("dashboard:stuck.retry")}>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={t("dashboard:stuck.retry")}
              disabled={retryAwb.isPending}
              onClick={() =>
                retryAwb.mutate(order.id, { onError: (e) => toast.error(apiErrorMessage(e)) })
              }
            >
              <RotateCw className={`h-4 w-4 ${retryAwb.isPending ? "animate-spin" : ""}`} />
            </Button>
          </Tooltip>
        )}
      </td>
    </tr>
  );
}

/**
 * Stuck-after-pick: orders confirm-picked with no AWB yet, from
 * `fulfillment.stuck_orders`. Cause cards on top carry the fix; the table
 * is the per-order drill-down. Seller-Own-Fleet rows are excluded by default
 * (a different ops workflow — SOF couriers are dispatched manually).
 */
export function StuckTable(): JSX.Element {
  const { t } = useTranslation("dashboard");
  const now = new Date();
  const [includeSof, setIncludeSof] = useState(false);
  // Client-side paging: the snapshot is capped at 200 rows server-side, so
  // slicing here is cheaper than another endpoint. Cause cards group over
  // every visible row, not just the page.
  const [page, setPage] = useState({ offset: 0, limit: PAGE_SIZES[0]! });

  const fulfillment = useFulfillment();
  const allOrders = fulfillment.data?.stuck_orders ?? [];
  const rows = includeSof ? allOrders : allOrders.filter((o) => !o.is_seller_own_fleet);
  const showSla = rows.some((o) => o.ship_by !== null);
  const pageRows = rows.slice(page.offset, page.offset + page.limit);

  return (
    <SectionCard
      title={t("dashboard:stuck.title_count", { n: rows.length })}
      caption={t("dashboard:stuck.caption")}
      titleTestId="stuck-table-caption"
      actions={
        <label className="flex items-center gap-2 text-xs text-[var(--text-subtle)]">
          <input
            type="checkbox"
            checked={includeSof}
            onChange={(e) => {
              setIncludeSof(e.target.checked);
              setPage((p) => ({ ...p, offset: 0 }));
            }}
            className="h-4 w-4 cursor-pointer rounded"
            style={{ accentColor: "var(--brand-500)" }}
            aria-label={t("dashboard:stuck.include_sof")}
          />
          {t("dashboard:stuck.include_sof")}
        </label>
      }
    >
      {rows.length === 0 ? (
        <EmptyState title={t("dashboard:stuck.empty")} />
      ) : (
        <>
          <StuckCauseCards groups={groupStuck(rows)} />
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="stuck-table">
              <thead>
                <tr className="border-b border-[var(--premium-border)]">
                  <th className={TH_COL} scope="col">
                    {t("dashboard:stuck.col_order")}
                  </th>
                  <th className={TH_COL} scope="col">
                    {t("dashboard:stuck.col_status")}
                  </th>
                  <th className={TH_COL} scope="col">
                    {t("dashboard:stuck.col_stuck")}
                  </th>
                  {showSla && (
                    <th className={TH_COL} scope="col">
                      {t("dashboard:stuck.col_sla")}
                    </th>
                  )}
                  <th className={TH_COL} scope="col">
                    {t("dashboard:stuck.col_problem")}
                  </th>
                  <th className={TH_COL} scope="col" />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((order) => (
                  <StuckRow key={order.id} order={order} now={now} showSla={showSla} />
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > PAGE_SIZES[0]! && (
            <div className="border-t border-[var(--premium-border)] px-4 py-3">
              <Pagination
                total={rows.length}
                offset={page.offset}
                limit={page.limit}
                onChange={setPage}
                pageSizeOptions={PAGE_SIZES}
              />
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}
